import Smooch from 'smooch';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import { ChatbotTriggers, ChatbotMessengerState } from './ChatbotMessengerState.enum';

// eslint-disable-next-line import/no-cycle
import Communication from '../utils/Communication';
import Session from '../utils/Session';
import Util from '../utils/Util';
import Logger from '../utils/Logger';
import Analytics from '../utils/Analytics';

/**
 * Lookup object for sunshine conversations chat widget elements
 */
const ChatbotWidgetSelectors = Object.freeze({
	webMessengerContainerSelector: '#web-messenger-container',
	headerSelector: '#header',
	wrapperSelector: '#wrapper',
	closeButtonSelector: '.close-handle.brand-dark.close-hidden',
	conversationFooterSelector: '.conversation-group-footer',
	footerButtonSelector: '.conversation-group-footer button',
	headerTitleSelector: '.header-title',
	headerContentSelector: '.header-content',
	introductionPane: '.intro-pane',
	headerIcon: '.business-icon',
});

/**
 * Details based on brand that is required for chatbot initialization
 * @param {string} brandName
 * @return {object}
 */
const getBrandDetails = brandName => {
	if (brandName === 'Stamps.com') {
		return {
			brandLabel: 'stamps',
			brandColor: '11003A',
			conversationColor: '005499',
			businessIconUrl:
				'https://cdn.bfldr.com/KJUN0NMZ/at/mm39pbfwk8bp9t4jvprjgr6/Stamps_Primary_Symbol_Red_RGB.svg?auto=webp&format=svg',
			buttonIconUrl:
				'https://cdn.bfldr.com/KJUN0NMZ/at/spp7rk4npm6hbwn4tqw6sr/icon_stamps_chatbot_color-on-white.svg?auto=webp&format=svg&width=256&height=256',
		};
	} else {
		return {
			brandLabel: 'endicia',
			brandColor: '00a8cb',
			conversationColor: 'f69712',
			businessIconUrl: 'https://auctane.com/wp-content/uploads/2024/01/image-13.png',
			buttonIconUrl: 'https://auctane.com/wp-content/uploads/2024/01/image-13.png',
		};
	}
};
/**
 * Make a HTTP request to fetch current billing plan
 * @return {object}
 */
const getBillingPlans = async () => {
	const billingResponse = await Communication.billingPlans();
	const isSuccessful = Communication.isSuccessful(billingResponse);

	if (isSuccessful) {
		let currentPlan;

		billingResponse.data.BillingPlans.forEach(plan => {
			if (plan.Sku === billingResponse.data.CurrentPlanSku) {
				currentPlan = plan;
			}
		});
		return currentPlan;
	}
};

const getUserContactInfo = async () => {
	const response = await Communication.getUserContactInfo();
	const isSuccessful = Communication.isSuccessful(response);

	return isSuccessful ? response?.data : {};
};

const getMessagingOrigin = chatbotTrigger => {
	if (chatbotTrigger === ChatbotTriggers.FeedbackButton) {
		return 'feedback_button_webapp';
	} else if (chatbotTrigger === ChatbotTriggers.AddFunds) {
		return 'add_funds_modal_webapp';
	} else {
		return 'webapp';
	}
};

/**
 * Build metadata for SunShine Conversations
 * @param {string} accountInfo
 * @param {string} brandLabel
 * @param {string} page
 * @return {object}
 */
const getUserdata = async (accountInfo, brandLabel, page, trigger) => {
	const {
		customerId,
		userId,
		planDetails = {},
		meterDetails = {},
		customerDetails = {},
		corporationId,
		lifetimeFundingLimit,
	} = accountInfo;

	const { meterNumber } = meterDetails;
	const { planId } = planDetails;
	const { username, creationDate } = customerDetails;
	const planInfo = await getBillingPlans();
	const userAccountInfo = await getUserContactInfo();
	const { Name, Price } = planInfo;
	const { UserInfo: { EmailAddress: userEmail = '' } = {} } = userAccountInfo;

	return {
		brand: brandLabel,
		sellerId: customerId,
		planLevel: Name.toLowerCase(),
		planPrice: Price,
		planId,
		messagingOrigin: getMessagingOrigin(trigger),
		currentPage: page.toLowerCase(),
		billingCycleDate: Util.formatDate(
			Util.getBillingDate(Session.getInternalAccountInfo().BillingDayOfMonth)
		),
		startDate: Util.formatDate(Util.convertDate(new Date(creationDate))),
		meterId: meterNumber,
		bridgeId: userId,
		username,
		enterpriseCorpId: corporationId,
		maxFundingLimit: lifetimeFundingLimit,
		userEmail,
	};
};

/**
 * Check if the element is an Iframe
 * @param {HTMLElement} input
 * @return {boolean}
 */
const isIFrame = input => input !== null && input.tagName === 'IFRAME';

/**
 * Returns the iframe document
 * @return {HTMLElement}
 */
const getIframedDocument = () => {
	const iframe = document.querySelector(ChatbotWidgetSelectors.webMessengerContainerSelector);
	let iframedocument;

	if (!isIFrame(iframe)) return null;

	if (iframe.contentDocument) {
		iframedocument = iframe.contentDocument;
	} else if (iframe.contentWindow) {
		iframedocument = iframe.contentWindow.document;
	}
	return iframedocument;
};

/**
 * Get HTMLElements from chat window
 * @param {string} selector
 * @return {HTMLElement}
 */
const getIframeElements = selector => {
	const iframedocument = getIframedDocument();
	return iframedocument ? iframedocument.querySelectorAll(selector) : null;
};

/**
 * Get HTMLElement from chat window
 * @param {string} selector
 * @return {HTMLElement}
 */
const getIframeElement = selector => {
	const elements = getIframeElements(selector);
	return elements ? elements[0] : null;
};

/**
 * Opens the chat window
 */
const openWidget = () => {
	if (!Smooch.isOpened()) {
		Smooch.open();
	}
};

// Add legal disclaimer text
const addLegalDisclaimer = () => {
	const introductionPane = getIframeElement(ChatbotWidgetSelectors.introductionPane);

	if (!introductionPane) return;

	introductionPane.innerText =
		'By starting a chat with our artificial intelligence-powered assistant, you agree to the automated processing of your personal data.';
	introductionPane.style.backgroundColor = '#F7F7F8';
	introductionPane.style.fontFamily = 'Figtree, Arial, sans-serif';
	introductionPane.style.fontSize = '14px';
	introductionPane.style.lineHeight = '20px';
	introductionPane.style.padding = '16px';
	introductionPane.style.textAlign = 'center';
};

/**
 * load the conversation to UI based on conversationId
 * @param {string} conversationId
 */
const loadConversation = conversationId => {
	Smooch.loadConversation(conversationId).then(function() {
		addLegalDisclaimer();
	});
};

/**
 * Create new conversation
 * @param {Object} userData
 * @param {boolean} shouldLoadConversation
 */
const createConversation = (userData, shouldLoadConversation = false) => {
	Smooch.createConversation({
		metadata: {
			language: 'en-us',
			webpage: window.location.href,
			...userData,
		},
	}).then(function(conversation) {
		if (shouldLoadConversation) {
			loadConversation(conversation.id);
		}
	});
};

/**
 * Create new conversation and load the conversation in the chat window
 * @param {Object} userData
 */
const createAndloadConversation = userData => {
	if (Smooch.isOpened()) {
		createConversation(userData, true);
	}
};

/**
 * Create a minimize button to chat window header
 */
const addMinimizeButton = () => {
	const headerContent = getIframeElement('.header-content');

	if (!headerContent) return;

	const clickEvent = function(event) {
			event.preventDefault();
			event.stopPropagation();

			Session.savePreferences([
				{
					Name: 'ChatbotMessengerOpenState',
					Value: ChatbotMessengerState.MINIMIZED,
				},
			]);
			Smooch.close();
		},
		minimizeButton = document.createElement('div');

	minimizeButton.style.fontSize = '30px';
	minimizeButton.style.paddingLeft = '11px';
	minimizeButton.style.alignItems = 'end';
	minimizeButton.style.paddingBottom = '10px';
	minimizeButton.className = 'close-handle brand-dark';
	minimizeButton.innerText = '_';
	minimizeButton.addEventListener('click', clickEvent);
	headerContent.after(minimizeButton);
};

/**
 * Update any element styles for chat window
 * @param {object} userData
 */
const updateWidgetStyles = userData => {
	const iframedocument = getIframedDocument();

	if (iframedocument) {
		iframedocument.addEventListener(
			'click',
			function() {
				const conversationFooter = getIframeElement(
					ChatbotWidgetSelectors.conversationFooterSelector
				);
				// Customize New Conversation button
				if (conversationFooter) {
					Array.from(conversationFooter.children).forEach(function(child) {
						conversationFooter.removeChild(child);
					});
					const button = document.createElement('button');
					button.innerText = 'New Conversation';
					button.style.borderRadius = '2px';
					button.addEventListener('click', function() {
						createAndloadConversation(userData);
					});
					conversationFooter.appendChild(button);
				}

				setTimeout(() => {
					addLegalDisclaimer();
				}, 2000);
			},
			false
		);
	}

	const headerTitle = getIframeElement(ChatbotWidgetSelectors.headerTitleSelector),
		wrapper = getIframeElement(ChatbotWidgetSelectors.wrapperSelector),
		footerButton = getIframeElement(ChatbotWidgetSelectors.footerButtonSelector),
		headerIcon = getIframeElement(ChatbotWidgetSelectors.headerIcon);

	if (headerTitle) {
		headerTitle.style.color = '#ffffff';
		headerTitle.style.fontSize = '14px';
	}

	if (wrapper) wrapper.style.borderRadius = '2px';

	if (footerButton) footerButton.style.borderRadius = '2px';

	if (headerIcon) headerIcon.style.borderRadius = '0px';

	// Create a link element to include the Figtree font
	const link = iframedocument.createElement('link');
	link.rel = 'stylesheet';
	link.href =
		'https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&display=swap'; // Adjust weights as needed

	// Append the link element to the head
	iframedocument.head.appendChild(link);

	// Create a new <style> element
	const style = iframedocument.createElement('style');
	style.type = 'text/css';

	// Add your CSS rules, including !important
	style.appendChild(
		iframedocument.createTextNode(`
		body, #container #conversation .row .msg-wrapper .message-item {
			font-family: Figtree, Arial, sans-serif !important;
		}

		#container {
			color: #0F0F10;
			letter-spacing: normal;
			font-family: Figtree, Arial, sans-serif !important;
		}

		#container #wrapper {
			border-radius: 8px !important;
		}

		#container #conversation .row.right-row .msg-wrapper .msg {
			background-color: #CCEFFF !important;
		}

		#container #conversation .row .msg-wrapper .message-item {
			padding: 12px 16px;
			color: #0F0F10;
		}

		#container #conversation .conversation-timestamp-header {
			font-weight: normal;
			color: #8D9196;
			font-size: 14px;
			font-style: normal;
			font-weight: 400;
			line-height: 20px;
		}

		.header-title {
			font-family: Figtree, Arial, sans-serif;
		}

		.messages-container {
			padding: 24px 16px 0 24px;
		}

		.messenger-button-icon {
			display: flex;
			justify-content: center;
			align-items: center;
		}

		#container #conversation .from,
		.message-status-indicator .time {
			color: #56595D;
			font-size: 12px;
			font-style: normal;
			font-weight: 400;
			line-height: 16px;
		}

		#footer .input-container .message-input {
			font-family: Figtree, Arial, sans-serif;
			overflow-y: hidden;
		}

		.messenger-button-icon {
			background-color: #fff;
		}
	`)
	);

	// Append the style to the iframe's head or body
	iframedocument.head.appendChild(style);
};

/**
 * Customize the header element click events
 */
const modifyHeader = () => {
	const header = getIframeElement(ChatbotWidgetSelectors.headerSelector);

	if (!header) return;

	// By default, header click triggers close events. Trigger events only for icons
	header.addEventListener('click', function(event) {
		const headerContent = getIframeElement(ChatbotWidgetSelectors.headerContentSelector),
			headerTitle = getIframeElement(ChatbotWidgetSelectors.headerTitleSelector),
			isRandomHeaderClick = [header, headerTitle, headerContent].some(function(element) {
				return element === event.target;
			});

		if (isRandomHeaderClick) {
			event.stopPropagation();
			event.preventDefault();
		}
	});
	header.style.cursor = 'default';
};

const logSegmentTrackEvent = error => {
	const { Page } = Session.getSettings();
	const properties = {
		page: Page,
	};

	if (error) {
		properties.success = false;
		properties.error = error;
	} else {
		properties.success = true;
	}
	Analytics.trackEvent('ChatbotLaunch', properties, 'segment');
};

export default class ChatbotInitializer {
	static IsChatbotInitialized = false;

	static IsInitializationInProgress = false;

	/**
	 * Initialize SunShine Conversations Chatbot
	 * @param {{
	 *   chatbotState?: 'open' | 'minimized' | 'hide',
	 *   logTrackEvent?: boolean,
	 *   trigger?: 'feedbackBtn' | 'addFunds' | 'cancelAccount'
	 * }} chatbotParams
	 */
	static start = async (chatbotParams = {}) => {
		const sessionData = Session.getData();
		const { Page = '' } = Session.getSettings();

		if (sessionData.isSignedIn !== true) {
			return;
		}

		const chatWidget = this;
		const { ChatbotMessengerOpenState = '' } = Session.getPreferences();
		const { accountInfo, brand, settings } = sessionData;
		const { brandName } = brand;
		const { ChatbotIntegrationId, ChatbotToken } = settings;
		const { customerId, customerDetails = {} } = accountInfo;
		const { email = '', address = {} } = customerDetails;
		const chatbotState = isEmpty(chatbotParams)
			? ChatbotMessengerOpenState
			: chatbotParams.chatbotState;
		const logTrackEvent = !isEmpty(chatbotParams) ? chatbotParams.logTrackEvent : null;
		const chatbotTrigger = chatbotParams.trigger;

		const {
			brandLabel,
			brandColor,
			conversationColor,
			businessIconUrl,
			buttonIconUrl,
		} = getBrandDetails(brandName);

		const userData = await getUserdata(accountInfo, brandLabel, Page, chatbotTrigger);

		const nameParts = isString(address.name) ? address.name.split(' ') : [];
		const firstName = nameParts[0] || '';
		const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

		if (chatbotState !== ChatbotMessengerState.HIDE) {
			if (!this.IsChatbotInitialized) {
				if (!chatWidget.IsInitializationInProgress) {
					chatWidget.IsInitializationInProgress = true;
					Smooch.init({
						locale: 'en-US',
						integrationId: ChatbotIntegrationId,
						jwt: ChatbotToken,
						businessName: brandName === 'Stamps.com' ? 'Stamps.com' : 'endicia',
						businessIconUrl,
						buttonIconUrl,
						externalId: `${brandLabel}-${customerId}-${email}`,
						notificationChannelPromptEnabled: false,
						soundNotificationEnabled: false,
						customColors: {
							brandColor,
							actionColor: brandColor,
							conversationColor,
						},
					})
						.then(() => {
							chatWidget.IsChatbotInitialized = true;

							// update with user's account information
							Smooch.updateUser({
								email: userData?.userEmail || email,
								givenName: firstName,
								surname: lastName,
								metadata: userData,
							});
						})
						.catch(error => {
							chatWidget.IsInitializationInProgress = false;
							Logger.logToServer({
								category: 'page_view',
								message: 'Chatbot initialization error',
								error,
							});
							if (logTrackEvent) {
								logSegmentTrackEvent(error);
							}
							console.error('Chatbot initialization error:', error);
						})
						.then(function() {
							chatWidget.IsInitializationInProgress = false;

							if (chatWidget.IsChatbotInitialized) {
								chatWidget.bindChatbotEvents(userData);
								addMinimizeButton();
								if (chatbotState === ChatbotMessengerState.OPEN) {
									openWidget();
								}

								updateWidgetStyles(userData);
								modifyHeader();

								if (logTrackEvent) {
									logSegmentTrackEvent();
								}
							}
						});
				}
			} else if (chatbotState === ChatbotMessengerState.OPEN) {
				if (logTrackEvent) {
					logSegmentTrackEvent();
				}
				openWidget();
			}
		}
	};

	/**
	 * bind the Smooch events
	 * @param {object} userData
	 */
	static bindChatbotEvents = userData => {
		const chatWidget = this;

		// By default, 'X' minimizes the chat window.
		// Customize the event to destroy the chat window
		const modifyCloseButton = () => {
			const closeButton = getIframeElement(ChatbotWidgetSelectors.closeButtonSelector);

			if (!closeButton) return;

			const clickEvent = function(event) {
				event.preventDefault();
				event.stopPropagation();

				Session.savePreferences([
					{
						Name: 'ChatbotMessengerOpenState',
						Value: ChatbotMessengerState.HIDE,
					},
				]);
				chatWidget.IsChatbotInitialized = false;
				Smooch.destroy();
			};

			closeButton.addEventListener('click', clickEvent);
		};

		Smooch.on('widget:opened', () => {
			const { ChatbotMessengerConversationId = '' } = Session.getPreferences();

			modifyCloseButton();
			Session.savePreferences([
				{
					Name: 'ChatbotMessengerOpenState',
					Value: ChatbotMessengerState.OPEN,
				},
			]);

			const conversations = Smooch.getConversations();

			if (isArray(conversations)) {
				if (conversations.length === 0 || isEmpty(ChatbotMessengerConversationId)) {
					createAndloadConversation(userData);
				} else {
					conversations.forEach(conversation => {
						if (conversation.id === ChatbotMessengerConversationId) {
							loadConversation(ChatbotMessengerConversationId);
							return true;
						}
					});
				}
			}
		});

		Smooch.on('message', (data, source) => {
			const { ChatbotMessengerConversationId = '' } = Session.getPreferences();

			// When a message is sent, save conversationId to user's preferences
			if (
				source &&
				source.conversation &&
				ChatbotMessengerConversationId !== source.conversation.id
			) {
				Session.savePreferences([
					{
						Name: 'ChatbotMessengerConversationId',
						Value: source.conversation.id,
					},
				]);
			}
		});
	};

	/**
	 * Destroy the chatbot instance on signout
	 */
	static logout = () => {
		this.IsChatbotInitialized = false;
		if (Smooch && Smooch.destroy) {
			Smooch.destroy();
		}
	};

	/**
	 * Reset userpreferences
	 */
	static resetChatMessengerUserPrefs = () => {
		Session.savePreferences([
			{
				Name: 'ChatbotMessengerConversationId',
				Value: '',
			},

			{
				Name: 'ChatbotMessengerOpenState',
				Value: ChatbotMessengerState.HIDE,
			},
		]);
	};
}
